import {FlexRow, Modal, Text, TextField} from '@forecast-it/design-system';
import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../../forecast-app/shared/components/modals/types';

/**
 * Ceiling on a block's name, mirroring the column's VARCHAR(191) and the company service's own
 * check. Enforced here so an over-long name is refused in the dialog, with the name still in the
 * field: the modal closes the moment it hands the name over, so a server rejection arrives after
 * there is anywhere left to show it and surfaces as the generic error modal instead.
 */
export const MAX_CONTENT_BLOCK_NAME_CHARS = 191;

export type SaveContentBlockModalPayload = {
	/** The section whose copy is being saved, named in the explanation. */
	sectionLabel: string;
	onSave: (name: string) => void;
};

export const showSaveContentBlockModal = (props: SaveContentBlockModalPayload): void => {
	showModal({
		type: MODAL_TYPE.SAVE_CONTENT_BLOCK,
		...props,
	});
};

/** Names the current section copy so it can be reused on any quote in the account. */
export const SaveContentBlockModal = ({
	closeModal,
	sectionLabel,
	onSave,
}: ModalProps<SaveContentBlockModalPayload>): JSX.Element => {
	const intl = useIntl();
	const [name, setName] = useState('');
	const trimmed = name.trim();
	const tooLong = trimmed.length > MAX_CONTENT_BLOCK_NAME_CHARS;

	const handleSave = () => {
		if (!trimmed || tooLong) return;
		onSave(trimmed);
		closeModal();
	};

	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{intl.formatMessage({id: 'quote_designer.content_block.modal_title'})}</Modal.Title>
			<Modal.Content>
				<Text>{intl.formatMessage({id: 'quote_designer.content_block.modal_body'}, {sectionLabel})}</Text>
				<TextField
					label={intl.formatMessage({id: 'quote_designer.content_block.name_field'})}
					placeholder={intl.formatMessage({id: 'quote_designer.content_block.name_placeholder'})}
					value={name}
					onValueChange={setName}
					errorText={
						tooLong
							? intl.formatMessage(
									{id: 'quote_designer.content_block.name_too_long'},
									{max: MAX_CONTENT_BLOCK_NAME_CHARS}
							  )
							: undefined
					}
					data-cy="quote-content-block-name"
				/>
				<FlexRow>
					<Modal.PrimaryFooterButton
						onPress={handleSave}
						disabled={!trimmed || tooLong}
						dataCy="quote-content-block-confirm"
					>
						{intl.formatMessage({id: 'common.save'})}
					</Modal.PrimaryFooterButton>
					<Modal.SecondaryFooterButton onPress={closeModal} dataCy="cancel">
						{intl.formatMessage({id: 'common.cancel'})}
					</Modal.SecondaryFooterButton>
				</FlexRow>
			</Modal.Content>
		</Modal>
	);
};
